import React, { useRef, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Tooltip,
} from "@mui/material";
import { VirtualProductQuoteRequest } from "../../../models/virtualProduct/types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BranchAutocomplete } from "../branchAutocomplete";
import CommonHelper from "@/app/helpers/CommonHelper";

interface StepOneProps {
  form: VirtualProductQuoteRequest;
  setForm: React.Dispatch<React.SetStateAction<VirtualProductQuoteRequest>>;
  onNext: () => void;
}

const StepOne: React.FC<StepOneProps> = ({ form, setForm, onNext }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "PhoneNumber" ? CommonHelper.formatPhone334(value) : value,
    }));
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    onNext();
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const rawPhone = CommonHelper.normalizedPhone(form.PhoneNumber);

    if (!form.JobName.trim()) errors.jobName = "Required**";
    if (!form.MinDateNeedTime) errors.minDateNeedTime = "Required**";
    if (!form.ContactName) errors.contactName = "Required**";
    if (!rawPhone) {
      errors.phoneNumber = "Required**";
    } else if (rawPhone.length !== 10) {
      errors.phoneNumber = "Please enter a valid phone number.";
    }
    if (!form.EmailAddress.trim()) {
      errors.emailAddress = "Required**";
    } else if (!emailRegex.test(form.EmailAddress)) {
      errors.emailAddress = "Please enter a valid email address.";
    }

    return errors;
  };

  const dateInputRef = useRef<HTMLInputElement>(null);

  return (
    <Box className="vp-container">
      <Grid
        container
        columnSpacing={4}
        rowSpacing={2}
        alignItems="start"
        sx={{
          width: "100%",
          backgroundColor: "#e2e2e2",
          borderRadius: 2,
          px: 3,
          py: 4,
        }}
      >
        <Grid item xs={12} sm={6}>
          <label className="form-label" htmlFor="JobName">
            Job Name
          </label>
          <TextField
            id="JobName"
            name="JobName"
            value={form.JobName}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            className="form-input"
            size="small"
            required
            error={!!errors.jobName}
            helperText={errors.jobName}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <label
            className="form-label"
            htmlFor="MinDateNeedTime"
            style={{ display: "flex", alignItems: "center" }}
          >
            Date Order Needed
            <Tooltip
              title="Please note that unavailable dates are due to processing time required for this item"
              arrow
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ ml: 1, cursor: "pointer", color: "#1976d2" }}
              />
            </Tooltip>
          </label>
          <TextField
            id="MinDateNeedTime"
            name="MinDateNeedTime"
            value={form.MinDateNeedTime ?? ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            className="form-input"
            size="small"
            type="date"
            required
            error={!!errors.minDateNeedTime}
            helperText={errors.minDateNeedTime}
            inputRef={dateInputRef}
            onMouseDown={(e) => {
              e.preventDefault();
              const input = dateInputRef.current;
              if (input?.showPicker) input.showPicker();
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <label className="form-label" htmlFor="ContactName">
            Customer/Contact Name
          </label>
          <TextField
            id="ContactName"
            name="ContactName"
            value={form.ContactName}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            className="form-input"
            size="small"
            required
            error={!!errors.contactName}
            helperText={errors.contactName}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <label className="form-label" htmlFor="Branch">
            Branch
          </label>
          <BranchAutocomplete
            branchInitialValue={{ id: "0", displayName: form.Branch, value: "" }}
            onChange={(val) => {
              form.Branch = val?.displayName ?? "";
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <label className="form-label" htmlFor="PhoneNumber">
            Phone Number
          </label>
          <TextField
            id="PhoneNumber"
            name="PhoneNumber"
            value={CommonHelper.formatPhone334(form.PhoneNumber)}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            className="form-input"
            size="small"
            required
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset">
            <label className="form-label">Preferred Method of Contact</label>
            <RadioGroup
              row
              name="PreferredMethodofContact"
              value={form.PreferredMethodofContact}
              onChange={handleChange}
            >
              <FormControlLabel
                value="Phone"
                control={<Radio />}
                label={<span className="form-label-text">Phone</span>}
              />
              <FormControlLabel
                value="Email"
                control={<Radio />}
                label={<span className="form-label-text">Email</span>}
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <label className="form-label" htmlFor="EmailAddress">
            Email Address
          </label>
          <TextField
            id="EmailAddress"
            name="EmailAddress"
            value={form.EmailAddress}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            className="form-input"
            size="small"
            required
            error={!!errors.emailAddress}
            helperText={errors.emailAddress}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset">
            <label className="form-label">Application</label>
            <RadioGroup
              row
              name="ApplicationType"
              value={form.ApplicationType ?? ""}
              onChange={handleChange}
            >
              <FormControlLabel
                value="Replacement"
                control={<Radio />}
                label={<span className="form-label-text">Replacement</span>}
              />
              <FormControlLabel
                value="New Construction"
                control={<Radio />}
                label={
                  <span className="form-label-text">New Construction</span>
                }
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <div>
            <label className="form-label" htmlFor="JobLocation">
              Job Location
            </label>
            <TextField
              id="JobLocation"
              name="JobLocation"
              value={form.JobLocation}
              onChange={handleChange}
              variant="outlined"
              size="small"
              fullWidth
              className="form-input"
            />
          </div>

          <div style={{ marginTop: "16px" }}>
            <label className="form-label" htmlFor="JobDescription">
              Job Description
            </label>
            <TextField
              id="JobDescription"
              name="JobDescription"
              value={form.JobDescription}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              className="form-input"
              multiline
              minRows={3}
            />
          </div>
        </Grid>

        <Grid item xs={12} sm={6}>
          <label className="form-label" htmlFor="SpecialRequirements">
            Special Requirements/Comments
          </label>
          <TextField
            id="SpecialRequirements"
            name="SpecialRequirements"
            value={form.SpecialRequirements}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            className="form-input"
            multiline
            minRows={3}
          />
        </Grid>
      </Grid>

      <Button
        className="blueButtonOutline submit-button-blue"
        onClick={handleNext}
      >
        Next
      </Button>
    </Box>
  );
};

export default StepOne;
